











































































































import {Component, Mixins} from 'vue-mixin-decorator';
import {Prop, Vue, Watch} from 'vue-property-decorator';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Notify} from '@/utils/Notify';
import {FileStringReader} from '@/utils/FileReader';

import {ProfileSettings} from '@/model/network/settings/ProfileSettings';
import {OpenVpnSettings} from '@/model/network/settings/OpenVpnSettings';
import {Ip4Settings, Ip6Settings} from '@/model/network/settings/IpSettings';
import {VpnServiceType} from '@/model/network/settings/VpnSettings';

import {INetworkProfileService} from '@/services/network/INetworkProfileService';

import NetworkProfilesStore from '@/store/network/NetworkProfiles.store';

import {BaseComponent} from '@/mixins/BaseComponent';
import NetworkIpTabs from '@/components/Networking/Profile/NetworkIpTabs.vue';
import NetworkIpSettingsRoutesEditor from '@/components/Networking/Profile/NetworkIpSettingsRoutesEditor.vue';
import {EventBus} from '@/utils/EventBus';
import Event = EventBus.Event;
import {Profile} from '@/model/network/profile/Profile';

enum Step {
  ConnectionMethod = 1,
  GeneralSettings,
  AdvancedSettings,
}

@Component({
  components: {NetworkIpSettingsRoutesEditor, NetworkIpTabs},
})
export default class NetworkVpnAddWizard extends Mixins<BaseComponent>(BaseComponent) {
  private Step = Step;
  private VpnServiceType = VpnServiceType;

  @Inject(Symbol.for('INetworkProfileService'))
  private profileService!: INetworkProfileService;

  private profilesStore: NetworkProfilesStore = getModule(NetworkProfilesStore, this.$store);

  @Prop({default: false})
  private open!: boolean;

  private step: Step = Step.ConnectionMethod;
  private lastStep = Step.AdvancedSettings;

  private saving: boolean = false;

  private stepTitles: Map<Step, string> = new Map([
    [Step.ConnectionMethod, 'Connection method'],
    [Step.GeneralSettings, 'General settings'],
    [Step.AdvancedSettings, 'Advanced settings'],
  ]);

  private vpnType: VpnServiceType = VpnServiceType.OpenVpn;

  private profileName: string = '';

  private configurationFile: File | null = null;

  private username: string = '';

  private password: string = '';

  private certificatePassword: string = '';

  private settings: ProfileSettings = new ProfileSettings({
    IPv4: new Ip4Settings({
      NeverDefault: true,
    }),
    IPv6: new Ip6Settings({
      NeverDefault: true,
    }),
  });

  private async next() {

    this.saving = true;

    try {
      switch (this.step) {
        case Step.ConnectionMethod:
          break;

        case Step.GeneralSettings:
          this.checkGeneralSettings();
          break;

        case Step.AdvancedSettings:
          this.checkAdvancedSettings();
          const profile = await this.add();
          EventBus.Emit(Event.VpnAdded, profile);
          break;
      }

      if (this.step === this.lastStep) {
        this.close();
      } else {
        this.step++;
      }
    } catch (error) {
      Notify.Error(this.logger, `Error`, error);
    } finally {
      this.saving = false;
    }
  }

  private back() {
    this.step--;
  }

  @Watch('step')
  private async onStepChanged() {
    try {
      this.setLoading();
      switch (this.step) {
        default:
          break;
      }
    } catch (error) {
      Notify.Error(this.logger, `Load error`, error);
    } finally {
      this.setLoaded();
    }
  }

  private close() {
    this.step = Step.ConnectionMethod;
    this.$emit('update:open', false);
  }

  private checkGeneralSettings() {

    if (!this.profileName) {
      throw new Error('Missing profile name');
    }

    if (!this.configurationFile) {
      throw new Error('Missing .ovpn configuration file');
    }

    this.settings.Vpn = new OpenVpnSettings({
      Data: new Map<string, string>(),
      Secrets: new Map<string, string>(),
    });

    if (this.username !== '' || this.password !== '') {
      this.settings.Vpn.Data.set('username', this.username);
      this.settings.Vpn.Data.set('password-flags', '0');
      this.settings.Vpn.Secrets.set('password', this.password);
    }

    if (this.certificatePassword !== '') {
      this.settings.Vpn.Data.set('cert-pass-flags', '0');
      this.settings.Vpn.Secrets.set('cert-pass', this.certificatePassword);
    }
  }

  private checkAdvancedSettings() {
    if (!(this.$refs.routesEditor as Vue & { validate: () => boolean }).validate()) {
      throw new Error('You have an error in your routes configuration');
    }
  }

  private async add(): Promise<Profile> {

    if (!this.configurationFile) {
      throw new Error('Please add a valid .ovpn configuration file');
    }

    const fileContent = await FileStringReader.read(this.configurationFile);

    try {
      const vpnProfile = await this.profileService.import(
          this.profileName,
        this.vpnType,
        fileContent.split('\n'),
        this.settings,
      );

      this.logger.debug('VPN profile has been added', vpnProfile);

      this.profilesStore.addProfile(vpnProfile);

      return vpnProfile;
    } catch (e) {
      throw new Error(`could not import profile: ${e.message}`);
    }
  }
}
